import { Skip } from '@/src/utils/evaluateChain';
import { CachedStreamFetcher } from '@/src/core/streaming/cachedStreamFetcher';
import { Chunk } from '@/src/core/streaming/chunk';
import { DicomDataLoader } from '@/src/core/streaming/dicom/dicomDataLoader';
import {
  DicomMetaLoader,
  ReadDicomTagsFunction,
} from '@/src/core/streaming/dicom/dicomMetaLoader';
import { getRequestPool } from '@/src/core/streaming/requestPool';
import {
  ImportHandler,
  asIntermediateResult,
  asOkayResult,
} from '@/src/io/import/common';
import { getDicomDisplayOptions } from '@/src/io/import/dataSource';
import { getWorker } from '@/src/io/itk/worker';
import { FILE_EXT_TO_MIME } from '@/src/io/mimeTypes';
import { getErrorDetail } from '@/src/utils';
import { readDicomTags } from '@itk-wasm/dicom';
import { Tags } from '@/src/core/dicomTags';
import { useMessageStore } from '@/src/store/messages';
import {
  DICOM_FORCE_2D_STACK_ID_KEY,
  DICOM_FORCE_2D_STACK_INDEX_KEY,
  DICOM_FORCE_2D_STACK_KEY,
  DICOM_IGNORE_IMAGE_ORIENTATION_KEY,
} from '@/src/core/streaming/dicomDisplayOptions';

const handleDicomStream: ImportHandler = async (dataSource) => {
  if (dataSource.type !== 'uri' || dataSource?.mime !== FILE_EXT_TO_MIME.dcm) {
    return Skip;
  }

  const fetcher =
    dataSource.fetcher ??
    new CachedStreamFetcher(dataSource.uri, {
      fetch: (...args) => getRequestPool().fetch(...args),
    });

  const readTags: ReadDicomTagsFunction = async (file) => {
    try {
      const result = await readDicomTags(file, { webWorker: getWorker() });
      return result.tags;
    } catch (error) {
      const detail = getErrorDetail(
        error,
        'the file could not be parsed as valid DICOM (check browser console for details)'
      );
      throw new Error(
        `Failed to read DICOM tags from ${dataSource.uri}: ${detail}`,
        { cause: error }
      );
    }
  };

  const metaLoader = new DicomMetaLoader(fetcher, readTags);
  const dataLoader = new DicomDataLoader(fetcher);
  const chunk = new Chunk({
    metaLoader,
    dataLoader,
  });

  const dicomDisplayOptions = getDicomDisplayOptions(dataSource);
  if (dicomDisplayOptions?.ignoreImageOrientation) {
    chunk.setUserData(DICOM_IGNORE_IMAGE_ORIENTATION_KEY, true);
  }
  if (dicomDisplayOptions?.force2DStack) {
    chunk.setUserData(DICOM_FORCE_2D_STACK_KEY, true);
    chunk.setUserData(
      DICOM_FORCE_2D_STACK_ID_KEY,
      dicomDisplayOptions.force2DStackId
    );
    chunk.setUserData(
      DICOM_FORCE_2D_STACK_INDEX_KEY,
      dicomDisplayOptions.force2DStackIndex
    );
  }

  await chunk.loadMeta();

  const metadata = chunk.metadata;
  if (metadata) {
    const modalityEntry = metadata.find(([tag]) => tag === Tags.Modality);
    const modality = modalityEntry?.[1]?.trim();
    if (modality?.startsWith('RT')) {
      const messageStore = useMessageStore();
      messageStore.addWarning(
        `DICOM ${modality} modality is not supported. File ${dataSource.name} will be skipped.`
      );
      return asOkayResult(dataSource);
    }
  }

  return asIntermediateResult([
    {
      type: 'chunk',
      chunk,
      mime: FILE_EXT_TO_MIME.dcm,
      parent: dataSource,
    },
  ]);
};

export default handleDicomStream;
